import { useState } from 'react';
import { useChangePassword } from '../hooks/usePassword';
import { useAuthStore } from '../stores/authStore';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { PasswordInput } from '@/shared/components/PasswordInput';
import { Field, FieldLabel } from '@/components/ui/field';
import { useNotification } from '@/shared/hooks/useNotification';
import { LoadingAnimation } from '@/shared/components/LoadingAnimation';

interface ForcePasswordChangeDialogProps {
  isOpen: boolean;
  onSuccess: () => void;
}

export const ForcePasswordChangeDialog = ({
  isOpen,
  onSuccess,
}: ForcePasswordChangeDialogProps) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const changePasswordMutation = useChangePassword();
  const notify = useNotification();
  const { logout } = useAuthStore();

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!currentPassword.trim()) {
      newErrors.currentPassword = 'Current password is required';
    }

    if (!newPassword.trim()) {
      newErrors.newPassword = 'New password is required';
    }

    if (newPassword.length < 8) {
      newErrors.newPassword = 'Password must be at least 8 characters';
    }

    if (!confirmPassword.trim()) {
      newErrors.confirmPassword = 'Please confirm your password';
    }

    if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (currentPassword === newPassword) {
      newErrors.newPassword = 'New password must be different from current password';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    changePasswordMutation.mutate(
      {
        currentPassword,
        newPassword,
      },
      {
        onSuccess: () => {
          setCurrentPassword('');
          setNewPassword('');
          setConfirmPassword('');
          setErrors({});
          logout();
          notify.success('Password changed! Please log in with your new password.');
          onSuccess();
        },
        onError: (error: any) => {
          const errorMessage =
            error.response?.data?.message ||
            error.message ||
            'Failed to change password';
          notify.error(errorMessage);
          setErrors({
            submit: errorMessage,
          });
        },
      }
    );
  };

  return (
    <>
      {changePasswordMutation.isPending && (
        <LoadingAnimation fullScreen className="z-[100]" text="Changing password and sending confirmation…" />
      )}
      <Dialog open={isOpen} onOpenChange={() => {}}>
        <DialogContent
        className="w-[calc(100vw-2rem)] max-w-md max-h-[90vh] overflow-y-auto z-[70]"
        showCloseButton={false}
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Change Password</DialogTitle>
          <DialogDescription>
            Your password needs to be changed before you can continue. Please enter your current password and create a new one.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {errors.submit && (
            <div className="rounded-md bg-red-50 p-3 text-sm text-red-600">
              {errors.submit}
            </div>
          )}

          <Field>
            <FieldLabel htmlFor="currentPassword">
              Current Password <span className="text-red-500">*</span>
            </FieldLabel>
            <PasswordInput
              id="currentPassword"
              value={currentPassword}
              onChange={(e) => {
                setCurrentPassword(e.target.value);
                if (errors.currentPassword) {
                  setErrors((prev) => {
                    const { currentPassword: _, ...rest } = prev;
                    return rest;
                  });
                }
              }}
              disabled={changePasswordMutation.isPending}
              placeholder="Enter your current password"
            />
            {errors.currentPassword && (
              <div className="text-red-500 text-xs mt-1">
                {errors.currentPassword}
              </div>
            )}
          </Field>

          <Field>
            <FieldLabel htmlFor="newPassword">
              New Password <span className="text-red-500">*</span>
            </FieldLabel>
            <PasswordInput
              id="newPassword"
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value);
                if (errors.newPassword) {
                  setErrors((prev) => {
                    const { newPassword: _, ...rest } = prev;
                    return rest;
                  });
                }
              }}
              disabled={changePasswordMutation.isPending}
              placeholder="Enter a new password (minimum 8 characters)"
            />
            {errors.newPassword && (
              <div className="text-red-500 text-xs mt-1">
                {errors.newPassword}
              </div>
            )}
          </Field>

          <Field>
            <FieldLabel htmlFor="confirmPassword">
              Confirm Password <span className="text-red-500">*</span>
            </FieldLabel>
            <PasswordInput
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                if (errors.confirmPassword) {
                  setErrors((prev) => {
                    const { confirmPassword: _, ...rest } = prev;
                    return rest;
                  });
                }
              }}
              disabled={changePasswordMutation.isPending}
              placeholder="Confirm your new password"
            />
            {errors.confirmPassword && (
              <div className="text-red-500 text-xs mt-1">
                {errors.confirmPassword}
              </div>
            )}
          </Field>

          <Button
            type="submit"
            className="w-full"
            disabled={changePasswordMutation.isPending}
          >
            {changePasswordMutation.isPending ? 'Changing...' : 'Change Password'}
          </Button>
        </form>
        </DialogContent>
      </Dialog>
    </>
  );
};
